import { useCallback, useContext } from "react";
import { FinancialDocumentExtended } from "@common/types/financialDocument";
import { pluralizeWithCount } from "@common/helpers/string";
import { useAddFinancialDocumentsToImporterMutation } from "@api/financialDocument.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { AccrualImporterContext } from "./accrualImporter.context";
import { getAccrualDocumentImporterConfig } from "./accrualDocumentImporter.lib";

export function useAccrualDocumentMove() {
    const { mode, documentRows, removeDocumentRow } = useContext(
        AccrualImporterContext,
    );
    const targetMode = mode === "invoices" ? "bills" : "invoices";
    const { success, error } = useToaster();
    const move = useAddFinancialDocumentsToImporterMutation({
        importerQueue:
            getAccrualDocumentImporterConfig(targetMode).importerQueue,
        onSettled: ({ updatedDocuments, failedCount }) => {
            for (const updatedDocument of updatedDocuments) {
                const row = documentRows.find(
                    (documentRow) =>
                        documentRow.financialDocument?.id ===
                        updatedDocument.id,
                );
                if (row) {
                    removeDocumentRow(row);
                }
            }
            if (updatedDocuments.length > 0) {
                success(
                    `Moved ${pluralizeWithCount("draft", updatedDocuments.length)} to ${targetMode}`,
                );
            }
            if (failedCount > 0) {
                error(
                    `Failed to move ${pluralizeWithCount("draft", failedCount)}`,
                );
            }
        },
    });

    const moveDocuments = useCallback(
        (financialDocuments: FinancialDocumentExtended[]) => {
            if (financialDocuments.length > 0) {
                move.mutate(financialDocuments);
            }
        },
        [move],
    );

    return {
        moveDocuments,
        moveLabel: `Move to ${targetMode}`,
        isMoving: move.isPending,
    };
}
